import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Eye,
  FileDown,
  FilePlus,
  FileSpreadsheet,
  GripVertical,
  Loader2,
  Pencil,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import {
  employeeApi,
  roleApi,
  locationApi,
  positionApi,
  payrollDepartmentApi,
  scheduleApi,
  paymentTypeApi,
  paymentFrequencyApi,
  payRecordApi,
  employeeReportTemplatesApi,
  employeeDetailsReportRunsApi,
  leavePoliciesApi,
  type EmployeeReportTemplateDto,
} from '../services/api';
import type {
  Employee,
  Role,
  Location,
  Position,
  PayrollDepartment,
  Schedule,
  PaymentType,
  PaymentFrequency,
  PayRecord,
  EmployeeLicense,
  FeedbackRecord,
  TrainingRecord,
  EmployeeNote,
  LeavePolicy,
} from '../types';
import { useLocationContext } from '../components/Layout';
import EmployeeAdvancedFilterPanel from '../components/EmployeeAdvancedFilterPanel';
import NamePromptModal from '../components/NamePromptModal';
import MessageDialog, { type MessageDialogOpen } from '../components/MessageDialog';
import type { ReportHistoryItem } from '../components/ReportHistory';
import {
  FilterCondition,
  FilterFieldOption,
  buildEmployeeFilterFields,
  buildEmployeeReportFilterSummaries,
  filterEmployeesWithAdvancedFilters,
  getStatusFilterFromFilters,
} from '../utils/employeeAdvancedFilters';
import {
  DEFAULT_EMPLOYEE_REPORT_OUTPUT_KEYS,
  EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG,
  formatEmployeeReportCell,
  getDefaultLabelForOutputField,
} from '../utils/employeeReportOutputCatalog';
import { buildActivePayRecordMap } from '../utils/laborCost';
import { createCSVFile, createExcelFile, createPDFFile } from '../utils/reportExportFiles';
import { formatCompanyDateTime } from '../utils/companyDateFormat';

interface OutputColumnRow {
  id: string;
  fieldKey: string;
  headerLabel: string;
}

function newRowId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function refreshFilterIds(filters: FilterCondition[]): FilterCondition[] {
  return filters.map((f) => ({ ...f, id: newRowId() }));
}

function parseFiltersFromJson(json: string): FilterCondition[] {
  try {
    const a = JSON.parse(json) as FilterCondition[];
    return Array.isArray(a) ? refreshFilterIds(a) : [];
  } catch {
    return [];
  }
}

function defaultMandatoryFilters(): FilterCondition[] {
  return [
    { id: newRowId(), field: 'employeeReportStatus', value: 'Active' },
    { id: newRowId(), field: 'mainLocationId', value: '' },
  ];
}

/** Status + Main location always lead; remaining rows preserve order. */
function ensureLeadingMandatoryFilters(prev: FilterCondition[]): FilterCondition[] {
  const others = prev.filter((f) => f.field !== 'employeeReportStatus' && f.field !== 'mainLocationId');
  const statusRow = prev.find((f) => f.field === 'employeeReportStatus');
  const mainRow = prev.find((f) => f.field === 'mainLocationId');
  const status: FilterCondition = statusRow
    ? { ...statusRow, field: 'employeeReportStatus' }
    : { id: newRowId(), field: 'employeeReportStatus', value: 'Active' };
  let mainValue: string | number = '';
  if (mainRow) {
    const v = mainRow.value;
    if (v === null || v === undefined) mainValue = '';
    else if (typeof v === 'string' && v.trim() === '') mainValue = '';
    else mainValue = v as string | number;
  }
  const main: FilterCondition = mainRow
    ? { ...mainRow, field: 'mainLocationId', value: mainValue }
    : { id: newRowId(), field: 'mainLocationId', value: '' };
  return [status, main, ...others];
}

function normalizeApiStatusForFilterRow(api: string | undefined): 'Active' | 'InActive' | 'All' {
  if (!api) return 'Active';
  const s = String(api);
  if (s === 'InActive' || s === 'Inactive' || s === 'inactive') return 'InActive';
  if (s === 'All') return 'All';
  return 'Active';
}

function parseColumnsFromJson(json: string): { fieldKey: string; headerLabel: string }[] {
  try {
    const a = JSON.parse(json) as { fieldKey: string; headerLabel: string }[];
    return Array.isArray(a) ? a : [];
  } catch {
    return [];
  }
}

const defaultOutputColumns = (): OutputColumnRow[] =>
  DEFAULT_EMPLOYEE_REPORT_OUTPUT_KEYS.map((key) => ({
    id: newRowId(),
    fieldKey: key,
    headerLabel: getDefaultLabelForOutputField(key),
  }));

function revokeEmployeeReportBlobUrls(r: ReportHistoryItem | null): void {
  if (!r || r.reportType !== 'employee-details') return;
  [r.csvUrl, r.excelUrl, r.pdfUrl].forEach((u) => {
    if (u?.startsWith('blob:')) URL.revokeObjectURL(u);
  });
}

function resolveEmployeeDetailsSnapshot(
  r: ReportHistoryItem
): NonNullable<ReportHistoryItem['employeeDetailsSnapshot']> {
  const s = r.employeeDetailsSnapshot;
  if (!s?.headers?.length || !Array.isArray(s.dataRows)) {
    throw new Error('No saved row data for this run.');
  }
  return s;
}

function employeeDetailsExportBase(r: ReportHistoryItem): string {
  const d = new Date(r.createdAt);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `Employee_Details_Report_${y}-${m}-${day}`;
}

function formatEmployeeDetailsRunSummary(r: ReportHistoryItem): string {
  const p = r.parameters ?? {};
  const n = r.recordsProcessed ?? p.recordsProcessed ?? 0;
  const cols = p.outputColumnCount ?? 0;
  return `${n} row${n === 1 ? '' : 's'} · ${cols} col${cols === 1 ? '' : 's'}`;
}

export interface EmployeeDetailsReportRunPayload {
  headers: string[];
  rows: string[][];
  recordsProcessed: number;
  csvUrl: string;
  excelUrl: string;
  pdfUrl: string;
  parameters: {
    /** Derived from Status filter row */
    statusFilter: string;
    mainLocationSummary: string;
    advancedFilterCount: number;
    outputColumnCount: number;
    recordsProcessed: number;
    /** Full filter set as label + value for history */
    filterSummaries: Array<{ fieldKey: string; fieldLabel: string; valueText: string }>;
  };
}

interface EmployeeDetailsReportProps {
  onReportRun?: (payload: EmployeeDetailsReportRunPayload) => void | Promise<void>;
  /** Saved runs (from Reports page); shown inline like Early/Late report. */
  runHistory?: ReportHistoryItem[];
  onDeleteReport?: (reportId: string) => void;
}

const EmployeeDetailsReport: React.FC<EmployeeDetailsReportProps> = ({
  onReportRun,
  runHistory = [],
  onDeleteReport,
}) => {
  const {
    lockEmployeeDetailsReportLocation,
    unlockEmployeeDetailsReportLocation,
  } = useLocationContext();

  const [loading, setLoading] = useState(true);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [payrollDepartments, setPayrollDepartments] = useState<PayrollDepartment[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
  const [paymentFrequencies, setPaymentFrequencies] = useState<PaymentFrequency[]>([]);
  const [leavePolicies, setLeavePolicies] = useState<LeavePolicy[]>([]);

  const [filters, setFiltersState] = useState<FilterCondition[]>(() => defaultMandatoryFilters());

  const [templateList, setTemplateList] = useState<{ id: number; name: string }[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [loadedTemplateId, setLoadedTemplateId] = useState<number | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [columnsExpanded, setColumnsExpanded] = useState(true);

  const [allPayRecords, setAllPayRecords] = useState<PayRecord[]>([]);
  const [allLicenses, setAllLicenses] = useState<EmployeeLicense[]>([]);
  const [allFeedbackRecords, setAllFeedbackRecords] = useState<FeedbackRecord[]>([]);
  const [allTrainingRecords, setAllTrainingRecords] = useState<TrainingRecord[]>([]);
  const [allEmployeeNotes, setAllEmployeeNotes] = useState<EmployeeNote[]>([]);
  const [relatedDataLoading, setRelatedDataLoading] = useState(false);

  const [previewRows, setPreviewRows] = useState<Employee[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const [outputColumns, setOutputColumns] = useState<OutputColumnRow[]>(() => defaultOutputColumns());

  const [nameModal, setNameModal] = useState<{
    title: string;
    confirmLabel?: string;
    initialValue?: string;
    hint?: string;
    onConfirm: (name: string) => void | Promise<void>;
  } | null>(null);
  const [columnLabelsModalOpen, setColumnLabelsModalOpen] = useState(false);
  const [columnModalDraft, setColumnModalDraft] = useState<OutputColumnRow[]>([]);
  const [columnModalAddFieldKey, setColumnModalAddFieldKey] = useState('');
  const [filterEditModalOpen, setFilterEditModalOpen] = useState(false);
  const [filterModalDraft, setFilterModalDraftState] = useState<FilterCondition[]>(() => defaultMandatoryFilters());
  const [filterModalSaving, setFilterModalSaving] = useState(false);
  const [columnModalSaving, setColumnModalSaving] = useState(false);
  const [newTemplateWizardOpen, setNewTemplateWizardOpen] = useState(false);
  const [wizardName, setWizardName] = useState('');
  const [wizardFilters, setWizardFiltersState] = useState<FilterCondition[]>(() => defaultMandatoryFilters());
  const [wizardColumns, setWizardColumns] = useState<OutputColumnRow[]>(() => defaultOutputColumns());
  const [wizardAddFieldKey, setWizardAddFieldKey] = useState('');
  const [wizardSaving, setWizardSaving] = useState(false);
  const [draggingColumnIndex, setDraggingColumnIndex] = useState<number | null>(null);
  const [dragOverColumnIndex, setDragOverColumnIndex] = useState<number | null>(null);
  const [messageDialog, setMessageDialog] = useState<MessageDialogOpen>(null);
  const [detailHistoryReport, setDetailHistoryReport] = useState<ReportHistoryItem | null>(null);
  const [rowExportBusyId, setRowExportBusyId] = useState<string | null>(null);
  const setFilters = useCallback((action: React.SetStateAction<FilterCondition[]>) => {
    setFiltersState((prev) => {
      const next = typeof action === 'function' ? (action as (p: FilterCondition[]) => FilterCondition[])(prev) : action;
      return ensureLeadingMandatoryFilters(next);
    });
  }, []);

  const setWizardFilters = useCallback((action: React.SetStateAction<FilterCondition[]>) => {
    setWizardFiltersState((prev) => {
      const next = typeof action === 'function' ? (action as (p: FilterCondition[]) => FilterCondition[])(prev) : action;
      return ensureLeadingMandatoryFilters(next);
    });
  }, []);

  const setFilterModalDraft = useCallback((action: React.SetStateAction<FilterCondition[]>) => {
    setFilterModalDraftState((prev) => {
      const next = typeof action === 'function' ? (action as (p: FilterCondition[]) => FilterCondition[])(prev) : action;
      return ensureLeadingMandatoryFilters(next);
    });
  }, []);

  const activePayRecordByEmployeeId = useMemo(
    () => buildActivePayRecordMap(allPayRecords),
    [allPayRecords]
  );

  /** Synced inside `fetchFilteredEmployees` so Run report / same-tick formatting sees pay data before React re-renders. */
  const payRecordMapFromLastFetchRef = useRef<Record<number, PayRecord>>({});

  const formatReportCell = useCallback(
    (employee: Employee, fieldKey: string) =>
      formatEmployeeReportCell(employee, fieldKey, {
        activePayRecord:
          payRecordMapFromLastFetchRef.current[employee.id] ??
          activePayRecordByEmployeeId[employee.id],
      }),
    [activePayRecordByEmployeeId]
  );

  useEffect(() => {
    lockEmployeeDetailsReportLocation();
    return () => {
      unlockEmployeeDetailsReportLocation();
    };
  }, [lockEmployeeDetailsReportLocation, unlockEmployeeDetailsReportLocation]);

  const showAlert = (title: string, message: string, tone?: 'info' | 'success' | 'error') => {
    setMessageDialog({ variant: 'alert', title, message, tone });
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: { confirmLabel?: string; cancelLabel?: string; danger?: boolean }
  ) => {
    setMessageDialog({
      variant: 'confirm',
      title,
      message,
      onConfirm,
      confirmLabel: options?.confirmLabel ?? 'Delete',
      cancelLabel: options?.cancelLabel,
      tone: options?.danger ? 'danger' : 'default',
    });
  };

  const filterFields: FilterFieldOption[] = useMemo(
    () =>
      buildEmployeeFilterFields({
        locations,
        positions,
        payrollDepartments,
        schedules,
        roles,
        paymentTypes,
        paymentFrequencies,
        leavePolicies,
      }),
    [locations, positions, payrollDepartments, schedules, roles, paymentTypes, paymentFrequencies, leavePolicies]
  );

  const adHoc = loadedTemplateId == null;
  const loadedTemplateIdRef = useRef<number | null>(null);
  loadedTemplateIdRef.current = loadedTemplateId;

  const refreshTemplates = useCallback(async () => {
    const rows = await employeeReportTemplatesApi.list();
    setTemplateList(rows.map((r) => ({ id: r.id, name: r.name })));
  }, []);

  const columnsPayload = useCallback(
    () => outputColumns.map((c) => ({ fieldKey: c.fieldKey, headerLabel: c.headerLabel })),
    [outputColumns]
  );

  const applyTemplateDto = useCallback((d: EmployeeReportTemplateDto) => {
    let raw = parseFiltersFromJson(d.filtersJson);
    if (!raw.some((f) => f.field === 'employeeReportStatus')) {
      raw = [
        { id: newRowId(), field: 'employeeReportStatus', value: normalizeApiStatusForFilterRow(d.statusFilter) },
        ...raw,
      ];
    }
    setFilters(ensureLeadingMandatoryFilters(raw));

    const cols = parseColumnsFromJson(d.columnsJson);
    setOutputColumns(
      cols.length > 0
        ? cols.map((c) => ({
            id: newRowId(),
            fieldKey: c.fieldKey,
            headerLabel: c.headerLabel || getDefaultLabelForOutputField(c.fieldKey),
          }))
        : defaultOutputColumns()
    );
    setTemplateName(d.name);
    setLoadedTemplateId(d.id);
    setSelectedTemplateId(String(d.id));
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [
          employeesData,
          rolesData,
          locationsData,
          positionsData,
          payrollDepartmentsData,
          schedulesData,
          paymentTypesData,
          paymentFrequenciesData,
          leavePoliciesData,
        ] = await Promise.all([
          employeeApi.getAllFull(),
          roleApi.getAll(),
          locationApi.getAll(),
          positionApi.getAll(),
          payrollDepartmentApi.getAll(),
          scheduleApi.getAll(),
          paymentTypeApi.getAll(),
          paymentFrequencyApi.getAll(),
          leavePoliciesApi.getAll(),
        ]);
        setAllEmployees(employeesData);
        setRoles(rolesData);
        setLocations(locationsData);
        setPositions(positionsData);
        setPayrollDepartments(payrollDepartmentsData);
        setSchedules(schedulesData);
        setPaymentTypes(paymentTypesData);
        setPaymentFrequencies(paymentFrequenciesData);
        setLeavePolicies(Array.isArray(leavePoliciesData) ? leavePoliciesData : []);
      } catch (e) {
        console.error(e);
        setPreviewError('Failed to load employees or reference data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (loading) return;
    (async () => {
      setPresetsLoading(true);
      try {
        await refreshTemplates();
      } catch (e) {
        console.error(e);
        setPreviewError('Could not load saved report templates from the server.');
      } finally {
        setPresetsLoading(false);
      }
    })();
  }, [loading, refreshTemplates]);

  const computeRelatedNeeds = useCallback((f: FilterCondition[], cols: OutputColumnRow[]) => {
    const filterHasValue = (x: FilterCondition) => !!(x.value || x.valueFrom || x.valueTo);
    const colStartsWith = (prefix: string) =>
      cols.some((c) => String(c.fieldKey || '').toLowerCase().startsWith(prefix.toLowerCase()));
    const filterFieldStartsWith = (prefix: string) =>
      f.some(
        (x) =>
          String(x.field || '')
            .toLowerCase()
            .startsWith(prefix.toLowerCase()) && filterHasValue(x)
      );
    const needsPayRecords = filterFieldStartsWith('payRecord') || colStartsWith('payRecord');
    const needsLicenses = filterFieldStartsWith('license') || colStartsWith('license');
    const needsFeedback = filterFieldStartsWith('feedback') || colStartsWith('feedback');
    const needsTraining = filterFieldStartsWith('training') || colStartsWith('training');
    const needsNotes = filterFieldStartsWith('notes') || colStartsWith('notes');
    return { needsPayRecords, needsLicenses, needsFeedback, needsTraining, needsNotes };
  }, []);

  const ensureRelatedDataForFilters = async (
    f: FilterCondition[],
    cols: OutputColumnRow[],
    snapshot: {
      pay: PayRecord[];
      licenses: EmployeeLicense[];
      feedback: FeedbackRecord[];
      training: TrainingRecord[];
      notes: EmployeeNote[];
    }
  ) => {
    const { needsPayRecords, needsLicenses, needsFeedback, needsTraining, needsNotes } =
      computeRelatedNeeds(f, cols);
    if (!needsPayRecords && !needsLicenses && !needsFeedback && !needsTraining && !needsNotes) {
      return snapshot;
    }

    setRelatedDataLoading(true);
    try {
      const { default: axios } = await import('axios');
      const { getApiBaseUrl } = await import('../services/api');
      const baseURL = getApiBaseUrl();
      const authToken = localStorage.getItem('authToken');
      const authenticatedAxios = axios.create({
        baseURL,
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });

      let pay = snapshot.pay;
      let licenses = snapshot.licenses;
      let feedback = snapshot.feedback;
      let training = snapshot.training;
      let notes = snapshot.notes;

      if (needsPayRecords && pay.length === 0) {
        const payRecordsData = await payRecordApi.getAll();
        pay = Array.isArray(payRecordsData) ? payRecordsData : [];
        setAllPayRecords(pay);
      }
      if (needsLicenses && licenses.length === 0) {
        const res = await authenticatedAxios.get('/employee-licenses');
        licenses = Array.isArray(res.data) ? res.data : [];
        setAllLicenses(licenses);
      }
      if (needsFeedback && feedback.length === 0) {
        const res = await authenticatedAxios.get('/feedback-records');
        feedback = Array.isArray(res.data) ? res.data : [];
        setAllFeedbackRecords(feedback);
      }
      if (needsTraining && training.length === 0) {
        const res = await authenticatedAxios.get('/training-records');
        training = Array.isArray(res.data) ? res.data : [];
        setAllTrainingRecords(training);
      }
      if (needsNotes && notes.length === 0) {
        const res = await authenticatedAxios.get('/employee-notes');
        notes = Array.isArray(res.data) ? res.data : [];
        setAllEmployeeNotes(notes);
      }

      return { pay, licenses, feedback, training, notes };
    } finally {
      setRelatedDataLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- ensureRelatedDataForFilters is defined in render body
  const fetchFilteredEmployees = useCallback(async (): Promise<Employee[]> => {
    const snap = await ensureRelatedDataForFilters(filters, outputColumns, {
      pay: allPayRecords,
      licenses: allLicenses,
      feedback: allFeedbackRecords,
      training: allTrainingRecords,
      notes: allEmployeeNotes,
    });
    payRecordMapFromLastFetchRef.current = buildActivePayRecordMap(snap.pay);
    return filterEmployeesWithAdvancedFilters({
      allEmployees,
      selectedLocationId: null,
      statusFilter: getStatusFilterFromFilters(filters),
      filters,
      searchTerm: '',
      filterFields,
      sortColumn: 'name',
      sortDirection: 'asc',
      allPayRecords: snap.pay,
      allLicenses: snap.licenses,
      allFeedbackRecords: snap.feedback,
      allTrainingRecords: snap.training,
      allEmployeeNotes: snap.notes,
    });
  }, [
    allEmployees,
    filters,
    outputColumns,
    filterFields,
    allPayRecords,
    allLicenses,
    allFeedbackRecords,
    allTrainingRecords,
    allEmployeeNotes,
  ]);

  const handlePreviewReport = async () => {
    setPreviewError(null);
    try {
      const rows = await fetchFilteredEmployees();
      setPreviewRows(rows);
      setPreviewModalOpen(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Preview failed.';
      setPreviewError(msg);
      setPreviewModalOpen(false);
    }
  };

  const handleRunReport = async () => {
    if (!onReportRun) {
      showAlert('Not available', 'Report history is only available from the Reports page.', 'info');
      return;
    }
    setPreviewError(null);
    try {
      if (outputColumns.length === 0) {
        showAlert('No output columns', 'Add at least one output column before running the report.', 'error');
        return;
      }
      const rows = await fetchFilteredEmployees();
      setPreviewRows(rows);
      const headers = outputColumns.map((col) =>
        col.headerLabel.trim() || getDefaultLabelForOutputField(col.fieldKey)
      );
      const dataRows = rows.map((emp) =>
        outputColumns.map((col) => String(formatReportCell(emp, col.fieldKey) ?? ''))
      );
      const base = exportPreviewBaseFilename();
      const csv = createCSVFile(headers, dataRows, base);
      const excel = createExcelFile(headers, dataRows, base);
      const pdf = createPDFFile(headers, dataRows, base, { title: 'Employee details report' });
      const summaries = buildEmployeeReportFilterSummaries(filters, filterFields);
      const mainSummary = summaries.find((s) => s.fieldKey === 'mainLocationId');
      const statusSummary = summaries.find((s) => s.fieldKey === 'employeeReportStatus');
      await onReportRun({
        headers,
        rows: dataRows,
        recordsProcessed: rows.length,
        csvUrl: csv.url,
        excelUrl: excel.url,
        pdfUrl: pdf.url,
        parameters: {
          statusFilter: getStatusFilterFromFilters(filters),
          mainLocationSummary: mainSummary?.valueText ?? '—',
          advancedFilterCount: filters.length,
          outputColumnCount: outputColumns.length,
          recordsProcessed: rows.length,
          filterSummaries: summaries,
        },
      });
      showAlert(
        'Report added to history',
        'This run was saved to Report run history on this page. Open a row\'s Details or use CSV / Excel / PDF there to download again. Use Preview report anytime to review the current result set in a window.',
        'success'
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not run report.';
      setPreviewError(msg);
      showAlert('Run failed', msg, 'error');
    }
  };

  const exportPreviewBaseFilename = () => {
    const d = new Date().toISOString().slice(0, 10);
    return `Employee_Details_Report_${d}`;
  };

  const triggerFileDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  /** Same as triggering a download link without revoking the blob (history rows keep working for repeat downloads). */
  const triggerStoredBlobDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const sortedRunHistory = useMemo(
    () => [...runHistory].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [runHistory]
  );

  const mergeEmployeeDetailsRunWithServerSnapshot = useCallback(
    async (r: ReportHistoryItem): Promise<ReportHistoryItem> => {
      if (r.reportType !== 'employee-details') return r;
      const snap = r.employeeDetailsSnapshot;
      if (snap?.headers?.length && Array.isArray(snap.dataRows)) return r;
      if (r.serverEmployeeDetailsRunId == null) return r;
      const d = await employeeDetailsReportRunsApi.get(r.serverEmployeeDetailsRunId);
      return {
        ...r,
        parameters: (d.parameters as Record<string, unknown>) ?? r.parameters,
        employeeDetailsSnapshot: {
          headers: d.snapshot?.headers ?? [],
          dataRows: d.snapshot?.dataRows ?? [],
        },
      };
    },
    []
  );

  const closeHistoryDetails = useCallback(() => {
    setDetailHistoryReport((prev) => {
      revokeEmployeeReportBlobUrls(prev);
      return null;
    });
  }, []);

  const openHistoryDetails = useCallback(
    async (r: ReportHistoryItem) => {
      try {
        const row = await mergeEmployeeDetailsRunWithServerSnapshot(r);
        setDetailHistoryReport((prev) => {
          revokeEmployeeReportBlobUrls(prev);
          return row;
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Could not load saved run data.';
        setMessageDialog({ variant: 'alert', title: 'Load failed', message: msg, tone: 'error' });
      }
    },
    [mergeEmployeeDetailsRunWithServerSnapshot]
  );

  const handleHistoryRowExport = useCallback(
    async (kind: 'csv' | 'excel' | 'pdf', r: ReportHistoryItem) => {
      const baseName = employeeDetailsExportBase(r);
      if (kind === 'csv' && r.csvUrl) {
        triggerStoredBlobDownload(r.csvUrl, `${baseName}.csv`);
        return;
      }
      if (kind === 'excel' && r.excelUrl) {
        triggerStoredBlobDownload(r.excelUrl, `${baseName}.xlsx`);
        return;
      }
      if (kind === 'pdf' && r.pdfUrl) {
        triggerStoredBlobDownload(r.pdfUrl, `${baseName}.pdf`);
        return;
      }

      setRowExportBusyId(r.id);
      try {
        const row = await mergeEmployeeDetailsRunWithServerSnapshot(r);
        const snap = resolveEmployeeDetailsSnapshot(row);
        const { headers, dataRows } = snap;
        if (kind === 'csv') {
          const blob = createCSVFile(headers, dataRows, baseName);
          triggerFileDownload(blob.url, blob.filename);
        } else if (kind === 'excel') {
          const blob = createExcelFile(headers, dataRows, baseName);
          triggerFileDownload(blob.url, blob.filename);
        } else {
          const pdf = createPDFFile(headers, dataRows, baseName, { title: 'Employee details report' });
          triggerFileDownload(pdf.url, pdf.filename);
        }
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Could not build file from saved data.';
        setMessageDialog({ variant: 'alert', title: 'Export failed', message: msg, tone: 'error' });
      } finally {
        setRowExportBusyId(null);
      }
    },
    [mergeEmployeeDetailsRunWithServerSnapshot]
  );

  const buildPreviewExportTable = useCallback(() => {
    const headers = outputColumns.map((col) =>
      col.headerLabel.trim() || getDefaultLabelForOutputField(col.fieldKey)
    );
    const rows =
      previewRows?.map((emp) =>
        outputColumns.map((col) => String(formatReportCell(emp, col.fieldKey) ?? ''))
      ) ?? [];
    return { headers, rows };
  }, [outputColumns, previewRows, formatReportCell]);

  const handleExportPreviewExcel = () => {
    const { headers, rows } = buildPreviewExportTable();
    const { url, filename } = createExcelFile(headers, rows, exportPreviewBaseFilename());
    triggerFileDownload(url, filename);
  };

  const handleExportPreviewCsv = () => {
    const { headers, rows } = buildPreviewExportTable();
    const { url, filename } = createCSVFile(headers, rows, exportPreviewBaseFilename());
    triggerFileDownload(url, filename);
  };

  const handleExportPreviewPdf = () => {
    const { headers, rows } = buildPreviewExportTable();
    const { url, filename } = createPDFFile(headers, rows, exportPreviewBaseFilename(), {
      title: 'Employee details report',
    });
    triggerFileDownload(url, filename);
  };

  const openCreateTemplateModal = (initialName: string) => {
    setNameModal({
      title: 'Name for report template',
      confirmLabel: 'Save',
      initialValue: initialName,
      onConfirm: async (name) => {
        try {
          const created = await employeeReportTemplatesApi.create({
            name,
            statusFilter: getStatusFilterFromFilters(filters),
            filters: filters as unknown[],
            columns: columnsPayload(),
          });
          await refreshTemplates();
          applyTemplateDto(created);
          showAlert('Saved', 'Report template saved.', 'success');
        } catch (e) {
          console.error(e);
          showAlert('Save failed', 'Could not save the report template.', 'error');
        }
      },
    });
  };

  const clearReportTemplateSelection = useCallback(() => {
    setSelectedTemplateId('');
    setLoadedTemplateId(null);
    setTemplateName('');
    setFilters(defaultMandatoryFilters());
    setOutputColumns(defaultOutputColumns());
    setPreviewRows(null);
  }, []);

  const handleReportTemplateSelectChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    const previousLoadedId = loadedTemplateIdRef.current;
    if (!v) {
      clearReportTemplateSelection();
      return;
    }
    setSelectedTemplateId(v);
    try {
      const d = await employeeReportTemplatesApi.get(Number(v));
      applyTemplateDto(d);
      setPreviewRows(null);
    } catch (err) {
      console.error(err);
      showAlert('Load failed', 'Could not load the report template.', 'error');
      if (previousLoadedId != null) {
        setSelectedTemplateId(String(previousLoadedId));
        try {
          const d = await employeeReportTemplatesApi.get(previousLoadedId);
          applyTemplateDto(d);
        } catch {
          clearReportTemplateSelection();
        }
      } else {
        setSelectedTemplateId('');
      }
    }
  };

  const openNewTemplateWizard = () => {
    setWizardName('');
    setWizardFilters(defaultMandatoryFilters());
    setWizardColumns(defaultOutputColumns());
    setWizardAddFieldKey('');
    setNewTemplateWizardOpen(true);
  };

  const openFilterEditModal = () => {
    setFilterModalDraftState(ensureLeadingMandatoryFilters(filters.map((f) => ({ ...f }))));
    setFilterEditModalOpen(true);
  };

  const handleSaveFilterModal = async () => {
    const next = ensureLeadingMandatoryFilters(filterModalDraft);
    if (loadedTemplateId != null) {
      const name = templateName.trim();
      if (!name) {
        showAlert(
          'Name required',
          'Enter a template name in the output columns dialog (pencil), save it, then save filter changes here.',
          'info'
        );
        return;
      }
      setFilterModalSaving(true);
      try {
        await employeeReportTemplatesApi.update(loadedTemplateId, {
          name,
          statusFilter: getStatusFilterFromFilters(next),
          filters: next as unknown[],
          columns: columnsPayload(),
        });
        await refreshTemplates();
        setFilters(next);
        showAlert('Saved', 'Report filters were saved to this template.', 'success');
      } catch (e) {
        console.error(e);
        showAlert('Save failed', 'Could not save filters to the server.', 'error');
        return;
      } finally {
        setFilterModalSaving(false);
      }
    } else {
      setFilters(next);
    }
    setFilterEditModalOpen(false);
  };

  const openTemplateDetailsForColumns = () => {
    setColumnModalDraft(outputColumns.map((c) => ({ ...c })));
    setColumnModalAddFieldKey('');
    setColumnLabelsModalOpen(true);
  };

  const handleSaveColumnModal = async () => {
    if (columnModalDraft.length === 0) {
      showAlert('No output columns', 'Add at least one output column.', 'error');
      return;
    }
    const name = templateName.trim();
    if (loadedTemplateId != null && !name) {
      showAlert('Name required', 'Enter a template name before saving.', 'info');
      return;
    }
    if (loadedTemplateId != null) {
      setColumnModalSaving(true);
      try {
        await employeeReportTemplatesApi.update(loadedTemplateId, {
          name,
          statusFilter: getStatusFilterFromFilters(filters),
          filters: filters as unknown[],
          columns: columnModalDraft.map((c) => ({
            fieldKey: c.fieldKey,
            headerLabel: c.headerLabel,
          })),
        });
        await refreshTemplates();
        setOutputColumns(columnModalDraft);
        showAlert('Saved', 'Output columns were saved to this template.', 'success');
      } catch (e) {
        console.error(e);
        showAlert('Save failed', 'Could not save columns to the server.', 'error');
        return;
      } finally {
        setColumnModalSaving(false);
      }
    } else {
      setOutputColumns(columnModalDraft);
    }
    setColumnLabelsModalOpen(false);
  };

  const handleSaveTemplate = () => {
    if (!adHoc) return;
    openCreateTemplateModal(templateName);
  };

  const handleSaveAsNewTemplate = () => {
    setNameModal({
      title: 'Name for new report template',
      confirmLabel: 'Save',
      initialValue: '',
      onConfirm: async (name) => {
        try {
          const created = await employeeReportTemplatesApi.create({
            name,
            statusFilter: getStatusFilterFromFilters(filters),
            filters: filters as unknown[],
            columns: columnsPayload(),
          });
          await refreshTemplates();
          applyTemplateDto(created);
          showAlert('Created', 'Report template created.', 'success');
        } catch (e) {
          console.error(e);
          showAlert('Save failed', 'Could not save the report template.', 'error');
        }
      },
    });
  };

  const handleDeleteTemplate = () => {
    if (!selectedTemplateId) {
      showAlert('Select a template', 'Choose a saved report template to delete.', 'info');
      return;
    }
    const id = Number(selectedTemplateId);
    const name = templateList.find((x) => x.id === id)?.name ?? 'this template';
    showConfirm(
      'Delete template',
      `Delete saved report template "${name}"? This cannot be undone.`,
      () => {
        void (async () => {
          try {
            await employeeReportTemplatesApi.delete(id);
            await refreshTemplates();
            if (loadedTemplateId === id) {
              setLoadedTemplateId(null);
              setTemplateName('');
              setFilters(defaultMandatoryFilters());
              setOutputColumns(defaultOutputColumns());
            }
            setSelectedTemplateId('');
            showAlert('Deleted', 'The template was removed.', 'success');
          } catch (e) {
            console.error(e);
            showAlert('Delete failed', 'Could not delete the template.', 'error');
          }
        })();
      },
      { danger: true, confirmLabel: 'Delete', cancelLabel: 'Cancel' }
    );
  };

  const addColumnModalColumn = () => {
    if (!columnModalAddFieldKey) return;
    if (columnModalDraft.some((c) => c.fieldKey === columnModalAddFieldKey)) {
      showAlert('Already added', 'That field is already in the output list.', 'info');
      return;
    }
    setColumnModalDraft((prev) => [
      ...prev,
      {
        id: newRowId(),
        fieldKey: columnModalAddFieldKey,
        headerLabel: getDefaultLabelForOutputField(columnModalAddFieldKey),
      },
    ]);
    setColumnModalAddFieldKey('');
  };

  const removeColumnModalColumn = (id: string) => {
    setColumnModalDraft((prev) => prev.filter((c) => c.id !== id));
  };

  const reorderColumnModalDraft = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setColumnModalDraft((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  }, []);

  const addWizardOutputColumn = () => {
    if (!wizardAddFieldKey) return;
    if (wizardColumns.some((c) => c.fieldKey === wizardAddFieldKey)) {
      showAlert('Already added', 'That field is already in the output list.', 'info');
      return;
    }
    setWizardColumns((prev) => [
      ...prev,
      {
        id: newRowId(),
        fieldKey: wizardAddFieldKey,
        headerLabel: getDefaultLabelForOutputField(wizardAddFieldKey),
      },
    ]);
    setWizardAddFieldKey('');
  };

  const removeWizardOutputColumn = (id: string) => {
    setWizardColumns((prev) => prev.filter((c) => c.id !== id));
  };

  const reorderWizardColumns = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setWizardColumns((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  }, []);

  const handleSaveNewTemplateWizard = async () => {
    const name = wizardName.trim();
    if (!name) {
      showAlert('Name required', 'Enter a report template name.', 'info');
      return;
    }
    if (wizardColumns.length === 0) {
      showAlert('No output columns', 'Add at least one output column.', 'error');
      return;
    }
    const wf = ensureLeadingMandatoryFilters(wizardFilters);
    setWizardSaving(true);
    try {
      const created = await employeeReportTemplatesApi.create({
        name,
        statusFilter: getStatusFilterFromFilters(wf),
        filters: wf as unknown[],
        columns: wizardColumns.map((c) => ({ fieldKey: c.fieldKey, headerLabel: c.headerLabel })),
      });
      await refreshTemplates();
      applyTemplateDto(created);
      setNewTemplateWizardOpen(false);
      setPreviewRows(null);
      showAlert('Saved', 'Report template was created and loaded.', 'success');
    } catch (e) {
      console.error(e);
      showAlert('Save failed', 'Could not create the report template.', 'error');
    } finally {
      setWizardSaving(false);
    }
  };

  const handleColumnDragEnd = () => {
    setDraggingColumnIndex(null);
    setDragOverColumnIndex(null);
  };

  const handleColumnDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumnIndex(index);
  };

  const COLUMN_MODAL_DND = 'staffly/column-modal-index';

  const handleColumnModalDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData(COLUMN_MODAL_DND, String(index));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingColumnIndex(index);
  };

  const handleColumnModalDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData(COLUMN_MODAL_DND);
    const from = Number(raw);
    if (!Number.isNaN(from)) reorderColumnModalDraft(from, dropIndex);
    setDraggingColumnIndex(null);
    setDragOverColumnIndex(null);
  };

  const WIZARD_COL_DND = 'staffly/wizard-col-index';

  const handleWizardColumnDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData(WIZARD_COL_DND, String(index));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingColumnIndex(index);
  };

  const handleWizardColumnDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData(WIZARD_COL_DND);
    const from = Number(raw);
    if (!Number.isNaN(from)) reorderWizardColumns(from, dropIndex);
    setDraggingColumnIndex(null);
    setDragOverColumnIndex(null);
  };

  const showSave = adHoc;
  const showSaveAsNew = loadedTemplateId != null;

  const columnModalCatalogKeys = useMemo(() => new Set(columnModalDraft.map((c) => c.fieldKey)), [columnModalDraft]);
  const columnModalAddableFields = useMemo(
    () => EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG.filter((f) => !columnModalCatalogKeys.has(f.key)),
    [columnModalCatalogKeys]
  );

  const wizardCatalogKeys = useMemo(() => new Set(wizardColumns.map((c) => c.fieldKey)), [wizardColumns]);
  const wizardAddableFields = useMemo(
    () => EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG.filter((f) => !wizardCatalogKeys.has(f.key)),
    [wizardCatalogKeys]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 text-neutral-600">Loading…</div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <NamePromptModal
        open={nameModal != null}
        title={nameModal?.title ?? ''}
        confirmLabel={nameModal?.confirmLabel}
        initialValue={nameModal?.initialValue}
        hint={nameModal?.hint}
        onCancel={() => setNameModal(null)}
        onConfirm={async (name) => {
          const fn = nameModal?.onConfirm;
          setNameModal(null);
          if (fn) await fn(name);
        }}
      />

      <MessageDialog state={messageDialog} onClose={() => setMessageDialog(null)} />

      {filterEditModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="filter-edit-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !filterModalSaving) setFilterEditModalOpen(false);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 border-b border-neutral-200 px-4 py-3 shrink-0">
              <h3 id="filter-edit-modal-title" className="text-lg font-semibold text-neutral-900">
                Edit report filters
              </h3>
              <button
                type="button"
                disabled={filterModalSaving}
                onClick={() => setFilterEditModalOpen(false)}
                className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 flex-1 min-h-0">
              <EmployeeAdvancedFilterPanel
                filters={filterModalDraft}
                setFilters={setFilterModalDraft}
                filterFields={filterFields}
                relatedDataLoading={relatedDataLoading}
                excludeFieldValues={['employeeReportStatus', 'mainLocationId']}
                nonDeletableFieldValues={['employeeReportStatus', 'mainLocationId']}
                disableFieldSelectForFieldValues={['employeeReportStatus', 'mainLocationId']}
                onClearFiltersOverride={() => setFilterModalDraft(defaultMandatoryFilters())}
              />
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-4 py-3 shrink-0">
              <button
                type="button"
                disabled={filterModalSaving}
                onClick={() => setFilterEditModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={filterModalSaving}
                onClick={() => void handleSaveFilterModal()}
                className="px-4 py-2 text-sm font-medium text-white bg-neutral-800 rounded-md hover:bg-neutral-900 disabled:opacity-50"
              >
                {filterModalSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {newTemplateWizardOpen && (
        <div
          className="fixed inset-0 z-[101] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-template-wizard-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !wizardSaving) setNewTemplateWizardOpen(false);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[92vh] flex flex-col border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 border-b border-neutral-200 px-4 py-3 shrink-0">
              <h3 id="new-template-wizard-title" className="text-lg font-semibold text-neutral-900">
                New report template
              </h3>
              <button
                type="button"
                disabled={wizardSaving}
                onClick={() => setNewTemplateWizardOpen(false)}
                className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-6 flex-1 min-h-0">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Report template name</label>
                <input
                  type="text"
                  value={wizardName}
                  onChange={(e) => setWizardName(e.target.value)}
                  placeholder="e.g. Active staff — main office"
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md"
                />
              </div>
              <EmployeeAdvancedFilterPanel
                filters={wizardFilters}
                setFilters={setWizardFilters}
                filterFields={filterFields}
                relatedDataLoading={relatedDataLoading}
                excludeFieldValues={['employeeReportStatus', 'mainLocationId']}
                nonDeletableFieldValues={['employeeReportStatus', 'mainLocationId']}
                disableFieldSelectForFieldValues={['employeeReportStatus', 'mainLocationId']}
                onClearFiltersOverride={() => setWizardFilters(defaultMandatoryFilters())}
              />
              <div className="space-y-3 border-t border-neutral-200 pt-4">
                <p className="text-sm font-semibold text-neutral-900">Output columns</p>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Add field</label>
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      <select
                        value={wizardAddFieldKey}
                        onChange={(e) => setWizardAddFieldKey(e.target.value)}
                        className="w-full sm:flex-1 min-w-0 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white"
                      >
                        <option value="">Choose a field…</option>
                        {wizardAddableFields.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.defaultLabel}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={addWizardOutputColumn}
                        disabled={!wizardAddFieldKey}
                        className="shrink-0 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-start">
                  {wizardColumns.map((col, index) => {
                    const fieldLabel =
                      EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG.find((c) => c.key === col.fieldKey)?.defaultLabel ||
                      col.fieldKey;
                    const isDragging = draggingColumnIndex === index;
                    const isDropTarget =
                      dragOverColumnIndex === index &&
                      draggingColumnIndex !== null &&
                      draggingColumnIndex !== index;
                    return (
                      <div
                        key={col.id}
                        className="inline-block max-w-full"
                        onDragOver={(e) => handleColumnDragOver(e, index)}
                        onDrop={(e) => handleWizardColumnDrop(e, index)}
                      >
                        <div
                          draggable
                          onDragStart={(e) => handleWizardColumnDragStart(e, index)}
                          onDragEnd={handleColumnDragEnd}
                          className={`relative flex items-stretch rounded-lg border border-neutral-200 bg-neutral-50 text-left shadow-sm transition-[opacity,box-shadow] cursor-grab active:cursor-grabbing ${
                            isDragging ? 'opacity-60' : ''
                          } ${isDropTarget ? 'ring-2 ring-blue-500 ring-offset-1 z-10' : ''}`}
                        >
                          <div className="flex min-w-0 max-w-[14rem] items-center gap-1.5 py-2 pl-2 pr-8">
                            <GripVertical className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
                            <span className="truncate text-sm font-medium text-neutral-800" title={fieldLabel}>
                              {fieldLabel}
                            </span>
                          </div>
                          <button
                            type="button"
                            aria-label={`Remove ${fieldLabel}`}
                            className="absolute right-1 top-1 rounded p-0.5 text-neutral-500 transition-colors hover:bg-neutral-200 hover:text-neutral-900"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeWizardOutputColumn(col.id);
                            }}
                          >
                            <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 border-b border-neutral-200">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium text-neutral-700">Field</th>
                        <th className="text-left py-2 px-3 font-medium text-neutral-700">Column title</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wizardColumns.map((col) => (
                        <tr key={col.id} className="border-b border-neutral-100 last:border-0">
                          <td className="py-2 px-3 align-top text-neutral-800">
                            {EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG.find((c) => c.key === col.fieldKey)?.defaultLabel ||
                              col.fieldKey}
                            <span className="block text-xs text-neutral-500 font-mono mt-0.5">{col.fieldKey}</span>
                          </td>
                          <td className="py-2 px-3">
                            <input
                              type="text"
                              value={col.headerLabel}
                              onChange={(e) =>
                                setWizardColumns((prev) =>
                                  prev.map((c) => (c.id === col.id ? { ...c, headerLabel: e.target.value } : c))
                                )
                              }
                              className="w-full min-w-[8rem] px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-4 py-3 shrink-0">
              <button
                type="button"
                disabled={wizardSaving}
                onClick={() => setNewTemplateWizardOpen(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={wizardSaving}
                onClick={() => void handleSaveNewTemplateWizard()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {wizardSaving ? 'Saving…' : 'Save template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {columnLabelsModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="column-labels-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !columnModalSaving) setColumnLabelsModalOpen(false);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 border-b border-neutral-200 px-4 py-3 shrink-0">
              <h3 id="column-labels-modal-title" className="text-lg font-semibold text-neutral-900">
                Output columns
              </h3>
              <button
                type="button"
                disabled={columnModalSaving}
                onClick={() => setColumnLabelsModalOpen(false)}
                className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-4 flex-1 min-h-0">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Template name</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder={
                    loadedTemplateId != null ? 'Saved template name' : 'Optional — used when you save a template'
                  }
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md"
                />
              </div>
              <p className="text-sm text-neutral-600">
                Add fields, reorder badges, and set column titles. Changes apply when you click Save.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1 min-w-0">
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Add field</label>
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <select
                      value={columnModalAddFieldKey}
                      onChange={(e) => setColumnModalAddFieldKey(e.target.value)}
                      className="w-full sm:flex-1 min-w-0 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white"
                    >
                      <option value="">Choose a field…</option>
                      {columnModalAddableFields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.defaultLabel}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={addColumnModalColumn}
                      disabled={!columnModalAddFieldKey}
                      className="shrink-0 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
              {columnModalDraft.length === 0 ? (
                <p className="text-sm text-neutral-500">No output columns. Add at least one field.</p>
              ) : (
                <div className="flex flex-wrap gap-2 items-start">
                  {columnModalDraft.map((col, index) => {
                    const fieldLabel =
                      EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG.find((c) => c.key === col.fieldKey)?.defaultLabel ||
                      col.fieldKey;
                    const isDragging = draggingColumnIndex === index;
                    const isDropTarget =
                      dragOverColumnIndex === index &&
                      draggingColumnIndex !== null &&
                      draggingColumnIndex !== index;
                    return (
                      <div
                        key={col.id}
                        className="inline-block max-w-full"
                        onDragOver={(e) => handleColumnDragOver(e, index)}
                        onDrop={(e) => handleColumnModalDrop(e, index)}
                      >
                        <div
                          draggable
                          onDragStart={(e) => handleColumnModalDragStart(e, index)}
                          onDragEnd={handleColumnDragEnd}
                          className={`relative flex items-stretch rounded-lg border border-neutral-200 bg-neutral-50 text-left shadow-sm transition-[opacity,box-shadow] cursor-grab active:cursor-grabbing ${
                            isDragging ? 'opacity-60' : ''
                          } ${isDropTarget ? 'ring-2 ring-blue-500 ring-offset-1 z-10' : ''}`}
                        >
                          <div className="flex min-w-0 max-w-[14rem] items-center gap-1.5 py-2 pl-2 pr-8">
                            <GripVertical className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
                            <span className="truncate text-sm font-medium text-neutral-800" title={fieldLabel}>
                              {fieldLabel}
                            </span>
                          </div>
                          <button
                            type="button"
                            aria-label={`Remove ${fieldLabel}`}
                            className="absolute right-1 top-1 rounded p-0.5 text-neutral-500 transition-colors hover:bg-neutral-200 hover:text-neutral-900"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeColumnModalColumn(col.id);
                            }}
                          >
                            <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="border border-neutral-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium text-neutral-700">Field</th>
                      <th className="text-left py-2 px-3 font-medium text-neutral-700">Column title</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columnModalDraft.map((col) => (
                      <tr key={col.id} className="border-b border-neutral-100 last:border-0">
                        <td className="py-2 px-3 align-top text-neutral-800">
                          {EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG.find((c) => c.key === col.fieldKey)?.defaultLabel ||
                            col.fieldKey}
                          <span className="block text-xs text-neutral-500 font-mono mt-0.5">{col.fieldKey}</span>
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="text"
                            value={col.headerLabel}
                            onChange={(e) =>
                              setColumnModalDraft((prev) =>
                                prev.map((c) => (c.id === col.id ? { ...c, headerLabel: e.target.value } : c))
                              )
                            }
                            className="w-full min-w-[8rem] px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-4 py-3 shrink-0">
              <button
                type="button"
                disabled={columnModalSaving}
                onClick={() => setColumnLabelsModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={columnModalSaving}
                onClick={() => void handleSaveColumnModal()}
                className="px-4 py-2 text-sm font-medium text-white bg-neutral-800 rounded-md hover:bg-neutral-900 disabled:opacity-50"
              >
                {columnModalSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewModalOpen && previewRows !== null && (
        <div
          className="fixed inset-0 z-[102] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="employee-preview-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreviewModalOpen(false);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
              <div>
                <h3 id="employee-preview-modal-title" className="text-lg font-semibold text-neutral-900">
                  Preview records
                </h3>
                <p className="text-sm text-neutral-600 mt-0.5">
                  <span className="font-medium text-neutral-800">{previewRows.length}</span> employee
                  {previewRows.length === 1 ? '' : 's'} match.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleExportPreviewExcel}
                  disabled={outputColumns.length === 0}
                  className="px-3 py-2 text-sm font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 disabled:opacity-50"
                >
                  Save as Excel
                </button>
                <button
                  type="button"
                  onClick={handleExportPreviewCsv}
                  disabled={outputColumns.length === 0}
                  className="px-3 py-2 text-sm font-medium text-sky-800 bg-sky-50 border border-sky-200 rounded-md hover:bg-sky-100 disabled:opacity-50"
                >
                  Save as CSV
                </button>
                <button
                  type="button"
                  onClick={handleExportPreviewPdf}
                  disabled={outputColumns.length === 0}
                  className="px-3 py-2 text-sm font-medium text-rose-800 bg-rose-50 border border-rose-200 rounded-md hover:bg-rose-100 disabled:opacity-50"
                >
                  Save as PDF
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewModalOpen(false)}
                  className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
                  aria-label="Close preview"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="overflow-auto flex-1 min-h-0 p-4">
              {outputColumns.length === 0 ? (
                <p className="text-sm text-neutral-500">Add output columns to see data here.</p>
              ) : previewRows.length === 0 ? (
                <p className="text-sm text-neutral-500">No employees match the current filters.</p>
              ) : (
                <div className="border border-neutral-200 rounded-lg overflow-x-auto bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0">
                      <tr>
                        {outputColumns.map((col) => (
                          <th
                            key={col.id}
                            className="text-left py-2 px-3 font-medium text-neutral-700 whitespace-nowrap"
                          >
                            {col.headerLabel.trim() || getDefaultLabelForOutputField(col.fieldKey)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((emp) => (
                        <tr key={emp.id} className="border-b border-neutral-100 last:border-0">
                          {outputColumns.map((col) => (
                            <td
                              key={col.id}
                              className="py-2 px-3 text-neutral-800 whitespace-nowrap max-w-xs truncate"
                            >
                              {formatReportCell(emp, col.fieldKey)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-4 py-3">
              <button
                type="button"
                onClick={() => setPreviewModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-neutral-800 rounded-md hover:bg-neutral-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Employee details report</h1>
        <p className="text-sm text-neutral-600 mt-1">
          The header location is fixed to <span className="font-medium">All locations</span> on this page. Use Status
          and Main location in filters (Main location can be &quot;All locations&quot; or a specific site). Templates
          are stored on the server for your account.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Report template</label>
        <div className="flex flex-wrap items-center gap-1.5">
          <select
            value={selectedTemplateId}
            onChange={(e) => void handleReportTemplateSelectChange(e)}
            disabled={presetsLoading}
            className="min-w-[200px] flex-1 max-w-md px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white"
          >
            <option value="">Select a saved template…</option>
            {templateList.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            title="New report template"
            onClick={openNewTemplateWizard}
            className="inline-flex items-center justify-center p-2.5 border border-blue-200 bg-blue-50 rounded-md hover:bg-blue-100 text-blue-800"
          >
            <FilePlus className="h-5 w-5" />
          </button>
          <button
            type="button"
            title="Delete template"
            onClick={handleDeleteTemplate}
            className="inline-flex items-center justify-center p-2.5 border border-red-200 rounded-md hover:bg-red-50 text-red-700"
          >
            <Trash2 className="h-5 w-5" />
          </button>
          {showSave && (
            <button
              type="button"
              title="Save template"
              onClick={handleSaveTemplate}
              className="inline-flex items-center justify-center p-2.5 rounded-md bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="h-5 w-5" />
            </button>
          )}
          {showSaveAsNew && (
            <button
              type="button"
              title="Save as new template"
              onClick={handleSaveAsNewTemplate}
              className="inline-flex items-center justify-center p-2.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Copy className="h-5 w-5" />
            </button>
          )}
          {presetsLoading && <span className="text-xs text-neutral-500 self-center">Loading…</span>}
        </div>
        {templateName ? (
          <p className="text-sm text-neutral-700">
            Template name: <span className="font-medium text-neutral-900">{templateName}</span>
          </p>
        ) : null}
      </div>

      <div className="flex flex-col md:flex-row gap-6 md:items-stretch">
        <div className="w-full md:flex-1 md:min-w-0 flex flex-col border border-neutral-200 rounded-lg bg-white overflow-hidden">
          <div className="flex items-stretch border-b border-neutral-200 bg-neutral-50 shrink-0">
            <button
              type="button"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="flex-1 flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-neutral-100 min-w-0"
            >
              <span className="flex items-center gap-2 font-semibold text-neutral-900 min-w-0">
                {filtersExpanded ? <ChevronDown className="h-5 w-5 shrink-0" /> : <ChevronRight className="h-5 w-5 shrink-0" />}
                <span className="truncate">Filters</span>
              </span>
            </button>
            <button
              type="button"
              title="Edit report filters"
              onClick={openFilterEditModal}
              className="px-3 border-l border-neutral-200 text-neutral-700 hover:bg-neutral-100 flex items-center justify-center"
            >
              <Pencil className="h-5 w-5" />
            </button>
          </div>
          {filtersExpanded && (
            <div className="p-4">
              <EmployeeAdvancedFilterPanel
                filters={filters}
                setFilters={setFilters}
                filterFields={filterFields}
                relatedDataLoading={relatedDataLoading}
                readOnly
                excludeFieldValues={['employeeReportStatus', 'mainLocationId']}
                nonDeletableFieldValues={['employeeReportStatus', 'mainLocationId']}
                disableFieldSelectForFieldValues={['employeeReportStatus', 'mainLocationId']}
                onClearFiltersOverride={() => setFilters(defaultMandatoryFilters())}
              />
            </div>
          )}
        </div>

        <div className="w-full md:flex-1 md:min-w-0 flex flex-col border border-neutral-200 rounded-lg bg-white overflow-hidden">
          <div className="flex items-stretch border-b border-neutral-200 bg-neutral-50 shrink-0">
            <button
              type="button"
              onClick={() => setColumnsExpanded(!columnsExpanded)}
              className="flex-1 flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-neutral-100 min-w-0"
            >
              <span className="flex items-center gap-2 font-semibold text-neutral-900 min-w-0">
                {columnsExpanded ? <ChevronDown className="h-5 w-5 shrink-0" /> : <ChevronRight className="h-5 w-5 shrink-0" />}
                <span className="truncate">Output columns</span>
              </span>
            </button>
            <button
              type="button"
              title="Edit output columns"
              onClick={openTemplateDetailsForColumns}
              className="px-3 border-l border-neutral-200 text-neutral-700 hover:bg-neutral-100 flex items-center justify-center"
            >
              <Pencil className="h-5 w-5" />
            </button>
          </div>
          {columnsExpanded && (
            <div className="p-4 space-y-3">
              <p className="text-sm text-neutral-600">
                Use the pencil to add, remove, reorder columns, and edit titles or template name.
              </p>

              {outputColumns.length === 0 ? (
                <p className="text-sm text-neutral-500">No output columns yet. Use the pencil to configure columns.</p>
              ) : (
                <div className="flex flex-wrap gap-2 items-start">
                  {outputColumns.map((col) => {
                    const fieldLabel =
                      EMPLOYEE_REPORT_OUTPUT_FIELD_CATALOG.find((c) => c.key === col.fieldKey)?.defaultLabel ||
                      col.fieldKey;
                    return (
                      <div
                        key={col.id}
                        className="inline-flex max-w-full items-center rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-left shadow-sm"
                      >
                        <span className="truncate text-sm font-medium text-neutral-800 max-w-[14rem]" title={fieldLabel}>
                          {col.headerLabel.trim() || fieldLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handlePreviewReport}
            disabled={relatedDataLoading}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-60"
          >
            Preview report
          </button>
          <button
            type="button"
            onClick={handleRunReport}
            disabled={relatedDataLoading}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-60"
          >
            Run report
          </button>
        </div>
        {previewError && <p className="text-sm text-red-600">{previewError}</p>}
        {previewRows === null && !previewError && (
          <p className="text-sm text-neutral-500">
            Preview report opens a window with the current result set. Run report saves the same data to Report run
            history on this page with Excel, CSV, and PDF downloads.
          </p>
        )}
        {previewRows !== null && (
          <p className="text-sm text-neutral-600">
            Last preview / run: <span className="font-medium text-neutral-800">{previewRows.length}</span> employee
            {previewRows.length === 1 ? '' : 's'} match. Use Preview report again to reopen the preview window.
          </p>
        )}
      </section>

      {onReportRun != null && (
        <>
          <div className="space-y-2 border-t border-neutral-200 pt-6">
            <h3 className="text-sm font-semibold text-neutral-800">Report run history</h3>
            {sortedRunHistory.length === 0 ? (
              <p className="text-sm text-neutral-500 py-4 border border-dashed border-neutral-200 rounded-lg text-center">
                No runs yet. Configure filters and columns, then click Run report.
              </p>
            ) : (
              <div className="space-y-2 max-h-[min(60vh,480px)] overflow-y-auto pr-1">
                {sortedRunHistory.map((r) => (
                  <div
                    key={r.id}
                    className="bg-white border border-neutral-200 rounded-lg px-3 py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm"
                  >
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="font-medium text-neutral-900">
                        {formatCompanyDateTime(r.createdAt)}{' '}
                        <span className="text-neutral-500 font-normal">
                          ({formatEmployeeDetailsRunSummary(r)})
                        </span>
                      </div>
                      <div className="text-xs text-neutral-600 space-y-0.5">
                        <div>
                          <span className="font-medium text-neutral-700">Status:</span>{' '}
                          {r.parameters?.statusFilter ?? '—'} ·{' '}
                          <span className="font-medium text-neutral-700">Main location:</span>{' '}
                          {r.parameters?.mainLocationSummary ?? '—'}
                        </div>
                        <div>
                          Records: {r.recordsProcessed ?? '—'} · Columns:{' '}
                          {r.parameters?.outputColumnCount ?? '—'}
                        </div>
                        {Array.isArray(r.parameters?.filterSummaries) && r.parameters.filterSummaries.length > 0 && (
                          <ul className="mt-1.5 pl-3 list-disc text-neutral-600 space-y-0.5">
                            {r.parameters.filterSummaries.map((row: { fieldLabel: string; valueText: string }, i: number) => (
                              <li key={i}>
                                <span className="font-medium text-neutral-700">{row.fieldLabel}:</span> {row.valueText}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => void openHistoryDetails(r)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-neutral-800 bg-neutral-100 border border-neutral-200 rounded hover:bg-neutral-200"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Details
                      </button>
                      <button
                        type="button"
                        title="Download CSV"
                        disabled={rowExportBusyId === r.id}
                        onClick={() => void handleHistoryRowExport('csv', r)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50"
                      >
                        {rowExportBusyId === r.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                        CSV
                      </button>
                      <button
                        type="button"
                        title="Download Excel"
                        disabled={rowExportBusyId === r.id}
                        onClick={() => void handleHistoryRowExport('excel', r)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50"
                      >
                        {rowExportBusyId === r.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="h-3.5 w-3.5" />
                        )}
                        Excel
                      </button>
                      <button
                        type="button"
                        title="Download PDF"
                        disabled={rowExportBusyId === r.id}
                        onClick={() => void handleHistoryRowExport('pdf', r)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded hover:bg-rose-100 disabled:opacity-50"
                      >
                        {rowExportBusyId === r.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <FileDown className="h-3.5 w-3.5" />
                        )}
                        PDF
                      </button>
                      {onDeleteReport && (
                        <button
                          type="button"
                          onClick={() => onDeleteReport(r.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                          title="Remove from history"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {detailHistoryReport && (
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4 bg-black/40"
              role="dialog"
              aria-modal="true"
              aria-labelledby="employee-details-history-detail-title"
              onClick={closeHistoryDetails}
            >
              <div
                className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col border border-neutral-200 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-neutral-200 bg-neutral-50 shrink-0">
                  <div>
                    <h2 id="employee-details-history-detail-title" className="text-base font-semibold text-neutral-900">
                      Employee details — report run
                    </h2>
                    <p className="text-xs text-neutral-600 mt-1">
                      Run: {formatCompanyDateTime(detailHistoryReport.createdAt)} ·{' '}
                      {formatEmployeeDetailsRunSummary(detailHistoryReport)}
                    </p>
                    <p className="text-[11px] text-neutral-500 mt-1">
                      Data shown is from the saved run; nothing is recalculated when you open this window.
                    </p>
                    {Array.isArray(detailHistoryReport.parameters?.filterSummaries) &&
                      detailHistoryReport.parameters.filterSummaries.length > 0 && (
                        <ul className="mt-2 text-xs text-neutral-700 list-disc pl-4 space-y-0.5">
                          {detailHistoryReport.parameters.filterSummaries.map(
                            (row: { fieldLabel: string; valueText: string }, i: number) => (
                              <li key={i}>
                                <span className="font-medium text-neutral-800">{row.fieldLabel}:</span>{' '}
                                {row.valueText}
                              </li>
                            )
                          )}
                        </ul>
                      )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {detailHistoryReport.csvUrl && (
                      <a
                        href={detailHistoryReport.csvUrl}
                        download
                        className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded"
                      >
                        <Download className="h-3.5 w-3.5" /> CSV
                      </a>
                    )}
                    {detailHistoryReport.excelUrl && (
                      <a
                        href={detailHistoryReport.excelUrl}
                        download
                        className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded"
                      >
                        <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
                      </a>
                    )}
                    {detailHistoryReport.pdfUrl && (
                      <a
                        href={detailHistoryReport.pdfUrl}
                        download
                        className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded"
                      >
                        <FileDown className="h-3.5 w-3.5" /> PDF
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={closeHistoryDetails}
                      className="rounded-lg border border-neutral-200 p-2 text-neutral-600 hover:bg-neutral-100"
                      aria-label="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-auto min-h-0 p-4">
                  {detailHistoryReport.employeeDetailsSnapshot &&
                  detailHistoryReport.employeeDetailsSnapshot.headers.length > 0 &&
                  detailHistoryReport.employeeDetailsSnapshot.dataRows.length > 0 ? (
                    <div className="overflow-x-auto border border-neutral-200 rounded-lg">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-600 sticky top-0">
                          <tr>
                            {detailHistoryReport.employeeDetailsSnapshot.headers.map((h, i) => (
                              <th key={i} className="px-3 py-2 font-medium whitespace-nowrap">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {detailHistoryReport.employeeDetailsSnapshot.dataRows.map((row, ri) => (
                            <tr key={ri} className="border-b border-neutral-100 last:border-0">
                              {detailHistoryReport.employeeDetailsSnapshot!.headers.map((_, ci) => (
                                <td
                                  key={ci}
                                  className="px-3 py-2 text-neutral-800 whitespace-nowrap max-w-xs truncate"
                                >
                                  {row[ci] ?? ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-neutral-500">
                      No row data was saved for this run (empty snapshot or legacy entry).
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeDetailsReport;
